import React, { useState } from 'react';
import { CheckCircle, Shield, X, User, Mail, Phone, Calendar, MapPin, GraduationCap } from 'lucide-react';

const Button = ({ children, variant = 'primary', className = '', ...props }) => {
  const baseClasses = 'px-6 py-3 rounded-xl font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';
  const variants = {
    primary: 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700 focus:ring-purple-500',
    orange: 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700 focus:ring-orange-500'
  };
  
  return (
    <button 
      className={`${baseClasses} ${variants[variant]} ${className}`} 
      {...props}
    >
      {children}
    </button>
  );
};

const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        ></div>
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  );
};

const PricingSection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dob: '',
    phone: '',
    email: '',
    currentState: '',
    usCitizen: '',
    collegeDegree: '',
    collegeAttended: ''
  });
  const [errors, setErrors] = useState({});

  const handleGetStarted = (planType) => {
    setSelectedPlan(planType);
    setIsModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.dob) newErrors.dob = 'Date of birth is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.currentState.trim()) newErrors.currentState = 'Current state is required';
    if (!formData.usCitizen) newErrors.usCitizen = 'Please select an option';
    if (!formData.collegeDegree) newErrors.collegeDegree = 'Please select an option';

    return newErrors;
  };

  const handleSubmit = () => {
    const newErrors = validateForm();
    
    if (Object.keys(newErrors).length === 0) {
      // Form is valid - here you would typically send data to backend
      console.log('Form submitted:', { ...formData, selectedPlan });
      alert('Form submitted successfully! We will contact you soon.');
      setIsModalOpen(false);
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        dob: '',
        phone: '',
        email: '',
        currentState: '',
        usCitizen: '',
        collegeDegree: '',
        collegeAttended: ''
      });
    } else {
      setErrors(newErrors);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setErrors({});
  };

  const usStates = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
    'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
    'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
    'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
    'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
    'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
    'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
  ];

  return (
    <>
      <section id="section-2" className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h2 className="text-5xl lg:text-6xl font-bold mb-6 text-gray-900">
            Simple, transparent pricing
          </h2>
          <p className="text-xl text-gray-600 mb-16 max-w-2xl mx-auto">
            No hidden fees, no long-term contracts. Choose what works for you.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            
            {/* Free Plan */}
            <div className="bg-white rounded-3xl p-12 border-2 border-orange-200 shadow-xl relative">
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-8 inline-block">
                No Upfront Cost
              </div>
              
              <h3 className="text-3xl font-bold mb-6 text-gray-900">Success-Only Plan</h3>
              
              <div className="flex items-baseline justify-center mb-8">
                <span className="text-6xl font-bold text-gray-900">$0</span>
                <span className="text-xl text-gray-500 ml-2">/month</span>
              </div>
              
              <div className="bg-orange-50 rounded-2xl p-6 border border-orange-200 mb-8">
                <p className="text-lg text-gray-900">
                  <span className="font-semibold text-orange-600">Success Fee:</span> Only 6% of your first year's salary when you land the job
                </p>
              </div>

              <div className="space-y-4 mb-8 text-left">
                {[
                  "Complete LinkedIn profile overhaul",
                  "ATS-optimized resume redesign", 
                  "Hourly job applications to perfect roles",
                  "Interview scheduling and coordination",
                  "Regular updates 24/7"
                ].map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-green-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>

              <Button 
                variant="orange" 
                className="w-full text-lg py-4 mb-4"
                onClick={() => handleGetStarted('Success-Only Plan')}
              >
                Get Started Free
              </Button>
            </div>

            {/* Paid Plan */}
            <div className="bg-white rounded-3xl p-12 border-2 border-purple-200 shadow-xl relative">
              <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-8 inline-block">
                Most Popular
              </div>
              
              <h3 className="text-3xl font-bold mb-6 text-gray-900">Professional Plan</h3>
              
              <div className="flex items-baseline justify-center mb-8">
                <span className="text-6xl font-bold text-gray-900">$28</span>
                <span className="text-xl text-gray-500 ml-2">/month</span>
              </div>
              
              <div className="bg-amber-50 rounded-2xl p-6 border border-amber-200 mb-8">
                <p className="text-lg text-gray-900">
                  <span className="font-semibold text-amber-600">Success Fee:</span> Only 3% of your first year's salary when you land the job
                </p>
              </div>

              {/* The Zentry Guarantee */}
              <div className="bg-green-50 rounded-2xl p-6 border border-green-200 mb-8">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <Shield size={20} className="text-green-600" />
                  <h4 className="text-lg font-bold text-gray-900">The Zentry Guarantee</h4>
                </div>
                <div className="space-y-3 text-left">
                  <div className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">If you don't land interviews within 45 days, we keep working free until you do.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Transparent. No ghosting. No BS.</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4 mb-8 text-left">
                {[
                  "Everything in Success-Only Plan",
                  "Priority job applications",
                  "Personalized interview coaching",
                  "Salary negotiation support",
                  "Dedicated account manager"
                ].map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-green-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>

              <Button 
                className="w-full text-lg py-4 mb-4"
                onClick={() => handleGetStarted('Professional Plan')}
              >
                Get Started Today
              </Button>
              
              <p className="text-sm text-gray-500">No setup fees • Cancel anytime • 7-day free trial</p>
            </div>
          </div>
        </div>
      </section>

      {/* Modal */}
      <Modal isOpen={isModalOpen} onClose={closeModal}>
        <div className="p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">Get Started with {selectedPlan}</h3>
              <p className="text-gray-600 mt-1">Please fill out this quick questionnaire to begin your journey</p>
            </div>
            <button
              onClick={closeModal}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X size={24} className="text-gray-500" />
            </button>
          </div>

          {/* Form */}
          <div className="space-y-6">
            {/* Name Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <User size={16} />
                  First Name *
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border text-gray-700 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                    errors.firstName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your first name"
                />
                {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <User size={16} />
                  Last Name *
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border text-gray-700 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                    errors.lastName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your last name"
                />
                {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
              </div>
            </div>

            {/* DOB */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <Calendar size={16} />
                Date of Birth *
              </label>
              <input
                type="date"
                name="dob"
                value={formData.dob}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border text-gray-700 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                  errors.dob ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.dob && <p className="text-red-500 text-sm mt-1">{errors.dob}</p>}
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Phone size={16} />
                  Phone Number *
                </label>
                <input
                  type="number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border text-gray-700 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                    errors.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="(555) 123-4567"
                />
                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Mail size={16} />
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border text-gray-700 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="your.email@example.com"
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>
            </div>

            {/* Current State */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <MapPin size={16} />
                Current State of Residence *
              </label>
              <select
                name="currentState"
                value={formData.currentState}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-xl text-gray-700 focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                  errors.currentState ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select your state</option>
                {usStates.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
              {errors.currentState && <p className="text-red-500 text-sm mt-1">{errors.currentState}</p>}
            </div>

            {/* US Citizen */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-3 block">
                Are you a US Citizen? *
              </label>
              <div className="flex gap-6">
                <label className="flex text-gray-700 items-center">
                  <input
                    type="radio"
                    name="usCitizen"
                    value="yes"
                    checked={formData.usCitizen === 'yes'}
                    onChange={handleInputChange}
                    className="mr-2 text-purple-600 focus:ring-purple-500"
                  />
                  Yes
                </label>
                <label className="flex text-gray-700 items-center">
                  <input
                    type="radio"
                    name="usCitizen"
                    value="no"
                    checked={formData.usCitizen === 'no'}
                    onChange={handleInputChange}
                    className="mr-2 text-purple-600 focus:ring-purple-500"
                  />
                  No
                </label>
              </div>
              {errors.usCitizen && <p className="text-red-500 text-sm mt-1">{errors.usCitizen}</p>}
            </div>

            {/* College Degree */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-3 block">
                Do you have a college degree? *
              </label>
              <div className="flex gap-6">
                <label className="flex text-gray-700 items-center">
                  <input
                    type="radio"
                    name="collegeDegree"
                    value="yes"
                    checked={formData.collegeDegree === 'yes'}
                    onChange={handleInputChange}
                    className="mr-2 text-purple-600 focus:ring-purple-500"
                  />
                  Yes
                </label>
                <label className="flex text-gray-700 items-center">
                  <input
                    type="radio"
                    name="collegeDegree"
                    value="no"
                    checked={formData.collegeDegree === 'no'}
                    onChange={handleInputChange}
                    className="mr-2 text-purple-600 focus:ring-purple-500"
                  />
                  No
                </label>
              </div>
              {errors.collegeDegree && <p className="text-red-500 text-sm mt-1">{errors.collegeDegree}</p>}
            </div>

            {/* College Attended - Only show if they have a degree */}
            {formData.collegeDegree === 'yes' && (
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <GraduationCap size={16} />
                  College/University Attended
                </label>
                <input
                  type="text"
                  name="collegeAttended"
                  value={formData.collegeAttended}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter your college/university name"
                />
              </div>
            )}

            {/* Submit Button */}
            <div className="pt-6">
              <Button onClick={handleSubmit} className="w-full text-lg py-4">
                Submit Application
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default PricingSection;